import React, { FC, useState, useEffect, useRef } from "react";
import Axios from "axios";
import { Editor as TinyMce } from "tinymce";
import { Editor } from "@tinymce/tinymce-react";

import {
  Checkbox,
  DialogContent,
  FormControl,
  Grid,
  ListItemText,
  TextField,
  IconButton,
  Tooltip,
  FormControlLabel,
  ListItemIcon,
  Divider,
  Paper,
  Chip,
  ListItem,
  FormHelperText,
  Box,
  GlobalStyles,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import {
  EMensagemCategoriaTipo,
  IMensagem,
  IMensagemLink,
  IMensagemPerguntaFormatada,
  IUser,
  schemaMensagem,
  yupMensagem,
} from "@deltasge/marauders-map";
import {
  Link as LinkIcon,
  TrayArrowUp as UploadIcon,
  Plus as AddIcon,
} from "mdi-material-ui";

import { snack } from "components/GlobalSnackbar";
import { useDialog } from "hooks/useDialog";
import { useMobile } from "hooks/useMobile";
import { useAppSelector } from "store";
import {
  Anexos,
  DialogLink,
  DialogPergunta,
  MensagemAtribuicoes,
} from "./components";
import { Form, Input } from "assets/styleds";
import {
  IMensagemCategoria,
  IPreMensagemArquivo,
  utilMensagem as util,
} from "../utilMensagem";
import { useForm } from "hooks/useForm";
import { AlbumFotos } from "./components/AlbumFotos";
import { logEvent } from "firebase/analytics";
import { analytics } from "App";
import { Backdrop } from "components/Backdrop";
import { getError } from "utils";

export const CadastroMensagem: FC<{
  mensagem?: IMensagem;
  formId: string;
  handleClose: () => void;
  categoria: IMensagemCategoria;
}> = ({ mensagem, formId, handleClose, categoria }) => {
  const CancelToken = Axios.CancelToken;
  const source = CancelToken.source();

  const {
    usuario: { usuario },
    escola,
  } = useAppSelector((state) => state);

  //#region States
  const [saving, setSaving] = useState(false);
  const [perguntaFormatada, setPerguntaFormatada] =
    useState<IMensagemPerguntaFormatada>();
  const [link, setLink] = useState<IMensagemLink>();
  const [editorStyle, setEditorStyle] = useState<string>();
  const editorRef = useRef<TinyMce | null>(null);
  //#endregion

  const {
    show: showPergunta,
    RenderDialog: RenderPergunta,
    hide: hidePergunta,
  } = useDialog({
    title: "Adicione uma resposta",
    options: {
      modal: true,
      maxWidth: "xs",
      scroll: "paper",
    },
  });

  const {
    show: showLink,
    RenderDialog: RenderLink,
    hide: hideLink,
  } = useDialog({
    title: "Adicione um link",
    options: {
      modal: true,
      maxWidth: "xs",
      scroll: "paper",
    },
  });

  const { data, handleChange, handleSubmit, errors, setValue } =
    useForm<IMensagem>({
      trackSubmitter: true,
      initialValues: {
        ...yupMensagem.getDefault(),
        idMensagemCategoria: categoria.id,
        ...mensagem,
      },
      schema: schemaMensagem,
      onSubmit: async (payload, submitter) => {
        try {
          if (
            submitter &&
            typeof submitter.value == "string" &&
            submitter.value == "rascunho"
          ) {
            payload.rascunho = true;
          } else {
            payload.rascunho = false;
          }

          if (
            !payload.mensagemAtribuicoes ||
            payload.mensagemAtribuicoes.length == 0
          ) {
            throw new Error("Você deve selecionar ao menos uma turma");
          }

          if (!payload.texto) {
            throw new Error("Informe o texto da publicação");
          }

          payload.mensagemAtribuicoes = payload.mensagemAtribuicoes.map(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ({ curso, serie, turma, materia, id, ...rest }) => {
              rest.idMensagem = null;
              if (rest.mensagemAtribuicaoAlunos) {
                rest.mensagemAtribuicaoAlunos =
                  rest.mensagemAtribuicaoAlunos.map(
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    ({ aluno, ...rest }) => rest
                  );
              }
              if (rest.mensagemAtribuicaoResponsaveis) {
                rest.mensagemAtribuicaoResponsaveis =
                  rest.mensagemAtribuicaoResponsaveis.map(
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    ({ responsavel, ...rest }) => rest
                  );
              }
              if (rest.mensagemAtribuicaoLoginsExtras) {
                rest.mensagemAtribuicaoLoginsExtras =
                  rest.mensagemAtribuicaoLoginsExtras.map(
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    ({ loginExtra, ...rest }) => rest
                  );
              }
              if (rest.mensagemAtribuicaoProfessores) {
                rest.mensagemAtribuicaoProfessores =
                  rest.mensagemAtribuicaoProfessores.map(
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    ({ professor, ...rest }) => rest
                  );
              }
              return rest;
            }
          );

          payload.mensagemCategoria = undefined;

          if (!payload.permiteResposta && payload.notificaResposta) {
            payload.notificaResposta = false;
          }

          setSaving(true);
          await util.handleSubmitMensagem({
            payload,
            cancelToken: source.token,
            handleClose,
            usuario: usuario as IUser,
          });
          logEvent(analytics, "select_content", {
            content_type: "publicacao_categoria",
            content_id: categoria.nome,
          });

          const quemArr: string[] = [];
          if (payload.mensagemAtribuicoes.at(0)?.aluno) quemArr.push("Alunos");
          if (payload.mensagemAtribuicoes.at(0)?.responsavel)
            quemArr.push("Responsáveis");
          if (payload.mensagemAtribuicoes.at(0)?.loginExtra)
            quemArr.push("Logins do aluno");
          if (payload.mensagemAtribuicoes.at(0)?.professor)
            quemArr.push("Professores");
          const quem = quemArr.joinEOu("e");

          logEvent(analytics, "select_content", {
            content_type: "publicacao_enviar_para",
            content_id: quem,
          });

          setSaving(false);
        } catch (error) {
          snack.error(getError(error));
        }
      },
    });

  useEffect(() => {
    let ativo = true;
    if (!editorStyle) {
      (async () => {
        const response = await fetch(
          `${process.env.PUBLIC_URL}/tinymce-styles.css`
        );
        const editorStyle = await response.text();
        if (ativo) setEditorStyle(editorStyle);
      })();
    }
    return () => {
      ativo = false;
    };
  }, []);

  const isMd = useMobile("md");

  return (
    <Form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit(e);
      }}
      id={formId}
      sx={{ overflow: "hidden" }}
    >
      <GlobalStyles
        styles={{
          ".tox-tinymce-aux": {
            zIndex: "15000 !important",
          },
        }}
      />
      <RenderPergunta>
        <DialogPergunta
          idMensagem={data.id ?? null}
          pergunta={perguntaFormatada}
          set={(pergunta) =>
            util.handlePerguntaFormatada({
              hideDialog: hidePergunta,
              pergunta,
              setValue,
              perguntasFormatadas: data.perguntasFormatadas,
              oldPergunta: perguntaFormatada,
            })
          }
        />
      </RenderPergunta>
      <RenderLink>
        <DialogLink
          idMensagem={data.id ?? null}
          link={link}
          set={(newLink) =>
            util.handleLink({
              hideDialog: hideLink,
              link: newLink,
              setValue,
              links: data.mensagemLinks,
              oldLink: link,
            })
          }
        />
      </RenderLink>
      <Box sx={{ height: "100%", width: "100%", overflow: "auto" }}>
        <DialogContent style={{ padding: 0 }}>
          <Divider />
          <Box pl={2} pr={2} pt={2}>
            <Grid container spacing={2}>
              <Grid item sm={12} md={7}>
                <Grid container spacing={2} alignItems="stretch">
                  <Grid item xs={12}>
                    <TextField
                      value={data.titulo}
                      onChange={handleChange("titulo")}
                      label={schemaMensagem.fields.titulo.spec.label}
                      helperText={errors.titulo}
                      error={!!errors.titulo}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl error={!!errors.texto}>
                      <Paper
                        variant="outlined"
                        sx={(theme) => ({
                          "& .tox-tinymce": {
                            zIndex: `${theme.zIndex.modal + 10000} !important`,
                            border: "none",
                            fontFamily: theme.typography.fontFamily,
                          },
                        })}
                      >
                        <Editor
                          tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`}
                          onInit={(evt, editor) => (editorRef.current = editor)}
                          onBlur={(evt, editor) => {
                            setValue("texto", editor.getContent());
                          }}
                          onSetContent={(evt, editor) => {
                            setValue("texto", editor.getContent());
                          }}
                          initialValue={
                            data.texto ?? mensagem?.texto ?? undefined
                          }
                          init={{
                            placeholder: schemaMensagem.fields.texto.spec.label,
                            browser_spellcheck: true,
                            language_url: `${process.env.PUBLIC_URL}/tinymce_language_pt_BR.js`,
                            language: "pt_BR",
                            height: "300px",
                            menubar: false,
                            plugins: [
                              "anchor",
                              "autolink",
                              "link",
                              "lists",
                              "paste",
                              ...(escola.codigo == "0605"
                                ? [
                                    "accordion",
                                    "advlist",
                                    "autoresize",
                                    "autosave",
                                    "charmap",
                                    "code",
                                    "codesample",
                                    "directionality",
                                    "emoticons",
                                    "fullscreen",
                                    "help",
                                    "image",
                                    "importcss",
                                    "insertdatetime",
                                    "media",
                                    "nonbreaking",
                                    "pagebreak",
                                    "preview",
                                    "quickbars",
                                    "searchreplace",
                                    "table",
                                    "visualblocks",
                                    "visualchars",
                                    "wordcount",
                                  ]
                                : []),
                            ],
                            toolbar:
                              "undo redo | " +
                              "bold italic underline forecolor | alignleft aligncenter " +
                              "alignright alignjustify | bullist numlist " +
                              "removeformat | link" +
                              (escola.codigo == "0605"
                                ? " | accordion accordionremove | blocks fontfamily fontsize | strikethrough | image | table media | lineheight outdent indent| backcolor | charmap emoticons | code fullscreen preview | print | pagebreak anchor codesample | ltr rtl"
                                : ""),
                            content_style: editorStyle,
                            promotion: false,
                            branding: false,
                            resize: false,
                            contextmenu: false,
                            paste_as_text: true,
                          }}
                        />
                      </Paper>
                      {errors.texto && (
                        <FormHelperText>{errors.texto}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    {categoria.tipo != EMensagemCategoriaTipo.Fotos && (
                      <Tooltip title="Criar link">
                        <IconButton
                          aria-label="criar-link"
                          onClick={() => {
                            setLink(undefined);
                            showLink();
                          }}
                        >
                          <LinkIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Fazer upload de arquivos">
                      <label htmlFor="icon-button-file">
                        <Input
                          accept={"image/*".concat(
                            categoria.tipo != EMensagemCategoriaTipo.Fotos
                              ? ", application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint, text/plain, application/pdf"
                              : ""
                          )}
                          id="icon-button-file"
                          type="file"
                          multiple
                          onChange={async (event) => {
                            const files = event.target.files;
                            if (files != null) {
                              const mensagemArquivos: IPreMensagemArquivo[] =
                                data.mensagemArquivos ?? [];
                              let ordem = 0;
                              for (const file of files) {
                                mensagemArquivos.push({
                                  file,
                                  idArquivo: "",
                                  idMensagem: data.id ?? null,
                                  ordem,
                                });
                                ordem++;
                              }
                              setValue("mensagemArquivos", mensagemArquivos);
                            }
                            event.target.files = null;
                          }}
                        />
                        <IconButton
                          aria-label="upload-arquivo"
                          component="span"
                        >
                          <UploadIcon />
                        </IconButton>
                      </label>
                    </Tooltip>
                  </Grid>

                  {(data.mensagemArquivos || data.mensagemLinks) && (
                    <Grid item xs={12}>
                      {categoria.tipo == EMensagemCategoriaTipo.Fotos && (
                        <AlbumFotos
                          fotos={
                            data.mensagemArquivos
                              ? (
                                  data.mensagemArquivos as IPreMensagemArquivo[]
                                ).map((m) => ({
                                  ...m,
                                  id: m.file
                                    ? URL.createObjectURL(m.file)
                                    : m.arquivo?.caminhoArquivo ?? "",
                                }))
                              : []
                          }
                          set={setValue}
                        />
                      )}
                      {categoria.tipo != EMensagemCategoriaTipo.Fotos && (
                        <Anexos
                          mensagemArquivos={(
                            data.mensagemArquivos ?? []
                          )?.filter((f) => !f.deletedAt)}
                          mensagemLinks={(data.mensagemLinks ?? []).filter(
                            (f) => !f.deletedAt
                          )}
                          set={setValue}
                          handleEditLink={(link) => {
                            setLink(link);
                            showLink();
                          }}
                        />
                      )}
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {!isMd && (
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ paddingLeft: 2 }}
                />
              )}

              <Grid item sm={12} md>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={data.programarEnvio ? 6 : 12}>
                        <FormControl>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={data.programarEnvio}
                                onChange={(e, checked) =>
                                  setValue("programarEnvio", checked)
                                }
                              />
                            }
                            label={
                              schemaMensagem.fields.programarEnvio.spec.label
                            }
                          />
                        </FormControl>
                      </Grid>
                      {data.programarEnvio && (
                        <Grid item xs={6}>
                          <DatePicker
                            DialogProps={{ fullWidth: false }}
                            label={schemaMensagem.fields.enviarEm.spec.label}
                            value={data.enviarEm}
                            onChange={(data) => {
                              if (data && !isNaN(data.getTime())) {
                                setValue("enviarEm", data);
                              }
                            }}
                            renderInput={(params) => <TextField {...params} />}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                  {categoria.tipo != EMensagemCategoriaTipo.Fotos && (
                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Divider />
                        </Grid>
                        <Grid item xs={data.permiteResposta ? 6 : 12}>
                          <FormControl>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={data.permiteResposta}
                                  onChange={(e, checked) => {
                                    if (!checked) {
                                      setValue("notificaResposta", checked);
                                    }
                                    setValue("permiteResposta", checked);
                                  }}
                                />
                              }
                              label={
                                schemaMensagem.fields.permiteResposta.spec.label
                              }
                            />
                          </FormControl>
                        </Grid>
                        {data.permiteResposta && (
                          <Grid item xs={6}>
                            <DateTimePicker
                              DialogProps={{ fullWidth: false }}
                              label={
                                schemaMensagem.fields.respostaAte.spec.label
                              }
                              value={data.respostaAte}
                              onChange={(data) => {
                                if (data && !isNaN(data.getTime())) {
                                  setValue("respostaAte", data);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField {...params} />
                              )}
                            />
                          </Grid>
                        )}
                        {data.permiteResposta && (
                          <Grid item xs={12}>
                            <Paper sx={{ p: 1 }} variant="outlined">
                              <ListItem
                                dense
                                disablePadding
                                divider={
                                  data.perguntasFormatadas &&
                                  data.perguntasFormatadas.length > 0
                                }
                                sx={
                                  data.perguntasFormatadas &&
                                  data.perguntasFormatadas.length > 0
                                    ? { marginBottom: 1 }
                                    : undefined
                                }
                              >
                                <ListItemIcon>
                                  <IconButton
                                    onClick={() => {
                                      if (data.respostaComAnexo) {
                                        snack.warning(
                                          "Respostas formatadas não podem ser usadas com anexos de resposta"
                                        );
                                        return;
                                      }
                                      setPerguntaFormatada(undefined);
                                      showPergunta();
                                    }}
                                  >
                                    <AddIcon color="primary" />
                                  </IconButton>
                                </ListItemIcon>
                                <ListItemText
                                  primary="Adicione uma resposta"
                                  secondary="Adicione as opções para respostas formatadas"
                                />
                              </ListItem>
                              {data.perguntasFormatadas &&
                                data.perguntasFormatadas.length > 0 && (
                                  <Grid
                                    container
                                    direction="row"
                                    justifyContent="center"
                                    spacing={2}
                                  >
                                    {data.perguntasFormatadas
                                      ?.filter((f) => !f.deletedAt)
                                      ?.map(
                                        (
                                          pergunta,
                                          index,
                                          perguntasFormatadas
                                        ) => (
                                          <Grid item key={`pergunta-${index}`}>
                                            <Chip
                                              label={pergunta.nome}
                                              variant="outlined"
                                              onClick={() => {
                                                setPerguntaFormatada(pergunta);
                                                showPergunta();
                                              }}
                                              onDelete={() => {
                                                util.handlePerguntaFormatada({
                                                  pergunta,
                                                  setValue,
                                                  hasDelete: true,
                                                  perguntasFormatadas,
                                                });
                                              }}
                                            />
                                          </Grid>
                                        )
                                      )}
                                  </Grid>
                                )}
                            </Paper>
                          </Grid>
                        )}
                        {data.permiteResposta && (
                          <Grid item xs={12}>
                            <FormControl>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={data.respostaComAnexo}
                                    onChange={(e, checked) => {
                                      if (
                                        checked &&
                                        (data.perguntasFormatadas ?? [])
                                          ?.length > 0
                                      ) {
                                        snack.warning(
                                          "Não é possível usar perguntas formatadas com anexos!"
                                        );
                                        checked = false;
                                      }
                                      setValue("respostaComAnexo", checked);
                                    }}
                                  />
                                }
                                label={
                                  schemaMensagem.fields.respostaComAnexo.spec
                                    .label
                                }
                              />
                            </FormControl>
                          </Grid>
                        )}
                        {data.permiteResposta && (
                          <Grid item xs={12}>
                            <Tooltip title="Marque para receber notificações quando os destinatários responderem">
                              <FormControl>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={data.notificaResposta}
                                      onChange={(e, checked) => {
                                        setValue("notificaResposta", checked);
                                      }}
                                    />
                                  }
                                  label={
                                    schemaMensagem.fields.notificaResposta.spec
                                      .label
                                  }
                                />
                              </FormControl>
                            </Tooltip>
                          </Grid>
                        )}
                        <Divider />
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Divider />
          <MensagemAtribuicoes
            atribuicoes={data.mensagemAtribuicoes || []}
            setValue={setValue}
          />
        </DialogContent>
      </Box>
      <Backdrop open={saving} />
    </Form>
  );
};
